'use client';

import { cn } from '@/shared/utils/formatting';
import { Loader2 } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { toast } from 'sonner';

import { sendOtpForSignIn } from '../_lib/send-otp';

interface OtpEmailFormProps {
  email: string;
  setEmail: (value: string) => void;
  isAnyLoading: boolean;
  onCodeSent: () => void;
  onSwitchToPassword: () => void;
}

const INPUT =
  'w-full h-11 bg-transparent border-0 border-b border-[#2a1c22] rounded-none px-0.5 py-3 text-base text-text-primary placeholder:text-text-tertiary outline-none transition-colors hover:border-[#4a2a33] focus:border-red-primary disabled:opacity-70';
const INPUT_ERR = 'border-red-primary';
const PRIMARY =
  'w-full rounded-[2px] bg-red-primary text-white uppercase tracking-[0.1em] text-xs font-bold py-[17px] transition-colors hover:bg-red-hover active:bg-[#a51420] disabled:opacity-55 disabled:cursor-not-allowed flex items-center justify-center gap-2';
const GHOST =
  'self-center border-b border-transparent text-[13px] font-medium text-text-secondary tracking-[0.01em] py-2.5 transition-colors hover:text-white hover:border-white/20 disabled:opacity-70';
const ERR = 'text-[12.5px] text-red-primary pl-2.5 border-l-2 border-red-primary';

export function OtpEmailForm({
  email,
  setEmail,
  isAnyLoading,
  onCodeSent,
  onSwitchToPassword,
}: OtpEmailFormProps) {
  const router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!email) {
      setError('Email is required');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email');
      return;
    }

    setSubmitting(true);
    const result = await sendOtpForSignIn(email);
    setSubmitting(false);

    if (result.kind === 'bounced') {
      toast.info('No active subscription on this email. Choose a plan to get started.');
      router.push(result.redirectTo);
      return;
    }
    if (result.kind === 'sent') {
      onCodeSent();
      return;
    }
    if (result.kind === 'rate_limited') {
      setError('Too many attempts. Please wait a moment and try again.');
      return;
    }
    setError(result.message);
  };

  const isLoading = submitting || isAnyLoading;

  return (
    <form method="post" onSubmit={handleSubmit} className="flex flex-col gap-3.5 my-8">
      <div className="flex flex-col gap-1.5">
        <label htmlFor="otp-email" className="text-[12.5px] font-medium text-text-secondary">
          Email
        </label>
        <input
          id="otp-email"
          type="email"
          name="email"
          placeholder="you@example.com"
          value={email}
          onChange={handleChange}
          disabled={isLoading}
          autoComplete="email"
          autoFocus
          className={cn(
            `${INPUT} ${error ? INPUT_ERR : ''}`,
            'focus-visible:outline-0! focus-visible:outline-offset-0!'
          )}
        />
        {error && <p className={ERR}>{error}</p>}
      </div>

      <button type="submit" disabled={isLoading} className={PRIMARY}>
        {submitting ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Sending
          </>
        ) : (
          'Send me a code'
        )}
      </button>

      <button type="button" onClick={onSwitchToPassword} disabled={isLoading} className={GHOST}>
        Use password instead
      </button>
    </form>
  );
}
